import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'radix-ui';
import { AlarmClock, Bell, Check, ExternalLink, X } from 'lucide-react';
import type { SnoozeChoice } from '@nexus/core';
import { completeFromNotification, snoozeNotification } from '@nexus/workspace';
import type { NotificationRecord } from '@nexus/store';
import { useStore, useStoreQuery } from '../db/context';
import { useTasks } from '../db/tasks';
import { isOpen } from '@nexus/core';
import { useUi } from '../state/ui';
import { cn } from '../lib/cn';
import { loadNotificationSettings } from './settings';
import { openNotification } from './scheduler';
import { useToasts } from './toasts';
import { CloseButton } from '../components/CloseButton';

const small = 'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-bg-hover';

/** Afvinken / Uitstellen (10 min, 1 uur, morgen, kies moment) / Openen. */
export function NotificationActions({ n, onDone }: { n: NotificationRecord; onDone?: () => void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [picking, setPicking] = useState(false);
  const snooze = async (choice: SnoozeChoice) => {
    await snoozeNotification(store, n, choice, await loadNotificationSettings(store));
    useToasts.getState().dismiss(n.id);
    onDone?.();
  };
  if (!n.taskId) {
    return (
      <div className="mt-1 flex gap-1">
        <button type="button" className={small} onClick={() => openNotification(store, n)}>
          <ExternalLink className="size-3" />
          {t('notifications.open')}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <button
        type="button"
        className={small}
        onClick={async () => {
          await completeFromNotification(store, n);
          await store.deleteNotifications([n.id]);
          useToasts.getState().dismiss(n.id);
          onDone?.();
        }}
      >
        <Check className="size-3" />
        {t('notifications.done')}
      </button>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={small}>
            <AlarmClock className="size-3" />
            {t('notifications.snooze')}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={4}
            className="z-[70] flex flex-col rounded-xl border border-border bg-bg p-1 text-sm shadow-pop"
          >
            <button
              type="button"
              className="rounded px-2 py-1 text-left hover:bg-bg-hover"
              onClick={() => void snooze({ minutes: 10 })}
            >
              {t('notifications.snooze10')}
            </button>
            <button
              type="button"
              className="rounded px-2 py-1 text-left hover:bg-bg-hover"
              onClick={() => void snooze({ minutes: 60 })}
            >
              {t('notifications.snooze60')}
            </button>
            <button
              type="button"
              className="rounded px-2 py-1 text-left hover:bg-bg-hover"
              onClick={() => void snooze('tomorrow')}
            >
              {t('notifications.snoozeTomorrow')}
            </button>
            {picking ? (
              <input
                type="datetime-local"
                autoFocus
                aria-label={t('notifications.snoozePick')}
                className="m-1 rounded-md border border-border bg-bg px-1"
                onChange={(e) =>
                  e.target.value && void snooze({ at: new Date(e.target.value).getTime() })
                }
              />
            ) : (
              <button
                type="button"
                className="rounded px-2 py-1 text-left hover:bg-bg-hover"
                onClick={() => setPicking(true)}
              >
                {t('notifications.snoozePick')}
              </button>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <button type="button" className={small} onClick={() => openNotification(store, n)}>
        <ExternalLink className="size-3" />
        {t('notifications.open')}
      </button>
    </div>
  );
}

/** Bell with unread count; the panel lists recent notifications (missed ones marked). */
export function NotificationCenter() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const { notificationsOpen, set } = useUi();
  const all = useStoreQuery((s) => s.listNotifications(100), [], ['notifications']);
  // A reminder for a task that is done meanwhile has nothing left to say.
  const tasks = useTasks();
  const doneIds = new Set(tasks?.filter((x) => !isOpen(x.status)).map((x) => x.id));
  const list = all?.filter((n) => !n.taskId || !doneIds.has(n.taskId));
  const unread = list?.filter((n) => !n.readAt).length ?? 0;
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  return (
    <Popover.Root open={notificationsOpen} onOpenChange={(o) => set({ notificationsOpen: o })}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('notifications.title', { count: unread })}
          className="relative grid size-8 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          aria-label={t('notifications.panel')}
          className="z-50 flex max-h-[70vh] w-[min(24rem,calc(100vw-1rem))] flex-col rounded-xl border border-border bg-bg shadow-pop"
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <h2 className="flex-1 text-sm font-semibold">{t('notifications.panel')}</h2>
            <button
              type="button"
              disabled={unread === 0}
              onClick={() => void store.markNotificationsRead()}
              className="rounded px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover disabled:opacity-40"
            >
              {t('notifications.markAllRead')}
            </button>
            <button
              type="button"
              disabled={!list?.length}
              onClick={() => void store.deleteNotifications()}
              className="rounded px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover disabled:opacity-40"
            >
              {t('notifications.clearAll')}
            </button>
            <Popover.Close asChild>
              <CloseButton className="size-7" />
            </Popover.Close>
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto p-1">
            {list?.length === 0 && (
              <li className="p-4 text-center text-sm text-fg-muted">{t('notifications.empty')}</li>
            )}
            {list?.map((n) => (
              <li
                key={n.id}
                className={cn('group relative rounded-lg px-2 py-2', !n.readAt && 'bg-bg-subtle')}
              >
                <button
                  type="button"
                  aria-label={t('notifications.remove', { title: n.title })}
                  title={t('notifications.remove', { title: n.title })}
                  onClick={() => void store.deleteNotifications([n.id])}
                  className="absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full text-fg-muted hover:bg-bg-hover hover:text-fg [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100"
                >
                  <X className="size-3.5" />
                </button>
                <div className="flex items-start gap-2">
                  <span
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      n.readAt ? 'bg-transparent' : n.kind === 'missed' ? 'bg-danger' : 'bg-accent',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="pr-6 text-sm font-medium">
                      {n.kind === 'missed' && (
                        <span className="mr-1 text-xs font-normal text-danger">
                          {t('notifications.missed')}
                        </span>
                      )}
                      {n.title}
                    </p>
                    {n.body && <p className="text-xs text-fg-muted">{n.body}</p>}
                    <p className="text-[11px] text-fg-muted">{fmt.format(n.fireAt)}</p>
                    <NotificationActions n={n} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Pop-ups for fresh reminders, bottom right. */
export function ToastHost() {
  const { t } = useTranslation();
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);
  const set = useUi((s) => s.set);
  return (
    <div
      className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2"
      role="region"
      aria-label={t('notifications.toasts')}
    >
      {toasts.map((n) => (
        <div
          key={n.id}
          role="status"
          className="pointer-events-auto rounded-xl border border-border bg-bg p-3 shadow-pop"
        >
          <div className="flex items-start gap-2">
            <Bell
              className={cn(
                'mt-0.5 size-4 shrink-0',
                n.kind === 'missed' ? 'text-danger' : 'text-accent-ink',
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{n.title}</p>
              {n.body && <p className="text-xs text-fg-muted">{n.body}</p>}
              {n.id.startsWith('missed-') ? (
                <button
                  type="button"
                  className={cn(small, 'mt-1')}
                  onClick={() => {
                    dismiss(n.id);
                    set({ notificationsOpen: true });
                  }}
                >
                  {t('notifications.show')}
                </button>
              ) : (
                <NotificationActions n={n} onDone={() => dismiss(n.id)} />
              )}
            </div>
            <button
              type="button"
              aria-label={t('app.dismiss')}
              onClick={() => dismiss(n.id)}
              className="rounded p-0.5 text-fg-muted hover:bg-bg-hover"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
